import React, { useState, useEffect } from 'react';
import { Table, Card, message, Typography, Tag, Button, Space } from 'antd';
import { EyeOutlined, ExpandOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { getAllDomains } from '../../services/api';

const { Title } = Typography;

const DomainList = React.memo(() => {
    const navigate = useNavigate();
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: isMobile ? 5 : 10,
        total: 0,
        showSizeChanger: !isMobile,
        showTotal: (total, range) => 
            isMobile ? `${total} total` : `${range[0]}-${range[1]} of ${total} domains`
    });

    const fetchDomains = async (page = 1, pageSize = pagination.pageSize) => {
        setLoading(true);
        try {
            const result = await getAllDomains(page, pageSize);
            
            if (result && result.data) {
                setDomains(result.data);
                setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize,
                    total: result.total || result.data.length
                }));
            }
        } catch (error) {
            console.error('Error fetching domains:', error);
            message.error('Failed to fetch domains');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDomains(pagination.current, pagination.pageSize);
    }, []); // Initial fetch

    const handleTableChange = (newPagination, filters, sorter) => {
        fetchDomains(newPagination.current, newPagination.pageSize);
    };

    const getStatusTag = (status, type = 'status') => (
        <Tag color={
            status === 'active' || status === 'completed' ? 'green' :
            status === 'expired' || status === 'failed' ? 'red' :
            'orange'
        }>
            {status.toUpperCase()}
        </Tag>
    );

    const mobileColumns = [
        {
            title: 'Domain Details',
            key: 'details',
            render: (record) => (
                <Space direction="vertical" size="small" className="w-full">
                    <div className="font-medium">{record.name}</div>
                    <Space wrap>
                        {getStatusTag(record.status)}
                        {record.payment_status && getStatusTag(record.payment_status, 'payment')}
                    </Space>
                    <div className="text-sm text-gray-500">
                        Expires: {record.expiry_date ? new Date(record.expiry_date).toLocaleDateString() : 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">
                        Price: {record.price ? `₹${record.price}` : 'N/A'}
                    </div>
                    <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/domains/${record.id}`)}
                        block
                    >
                        Manage
                    </Button>
                </Space>
            ),
        }
    ];

    const desktopColumns = [
        {
            title: 'Domain',
            dataIndex: 'name',
            key: 'name',
        },
        ...(isAdmin ? [{
            title: 'User',
            dataIndex: 'user_id',
            key: 'user_id',
        }] : []),
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusTag(status),
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            render: (price) => price ? `₹${price}` : 'N/A',
        },
        {
            title: 'Registration Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => new Date(date).toLocaleDateString(),
            responsive: ['lg'],
        },
        {
            title: 'Expiry Date',
            dataIndex: 'expiry_date',
            key: 'expiry_date',
            render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
        },
        {
            title: 'Payment Status',
            dataIndex: 'payment_status',
            key: 'payment_status',
            render: (status) => status ? getStatusTag(status, 'payment') : 'N/A',
            responsive: ['md'],
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/domains/${record.id}`)}
                >
                    Manage
                </Button>
            ),
        },
    ];

    return (
        <Card 
            title={<Title level={isMobile ? 3 : 2}>{isAdmin ? 'All Domains' : 'My Domains'}</Title>}
            className="overflow-hidden"
        >
            <Table
                columns={isMobile ? mobileColumns : desktopColumns}
                dataSource={domains}
                rowKey="id"
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ x: isMobile ? undefined : true }}
                className={isMobile ? 'ant-table-mobile' : ''}
            />
        </Card>
    );
});

export default DomainList;